import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Target, Plus, Trash2, Flame } from "lucide-react";

const METRICS = ["runs", "wickets", "average", "strike_rate", "economy", "matches"];

export default function Progress() {
  const t = useT();
  const [goals, setGoals] = useState(null);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ title: "", metric: "runs", target: "", focus_area: false });
  const [show, setShow] = useState(false);

  const load = async () => {
    const { data } = await api.get("/goals"); setGoals(data);
    const e = await api.get("/entries"); setEntries(e.data.slice(0, 5));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title || !form.target) return toast.error("Add a title and target");
    try { await api.post("/goals", { ...form, target: Number(form.target) }); setForm({ title: "", metric: "runs", target: "", focus_area: false }); setShow(false); load(); toast.success("Goal added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/goals/${id}`); load(); };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-bold text-3xl">{t("Progress")}</h1>
        <Button className="bg-brand hover:bg-brand-dark" onClick={() => setShow(!show)} data-testid="toggle-goal-form"><Plus size={16} /> {t("Goal")}</Button>
      </div>

      {show && (
        <div className="ct-card p-4 grid sm:grid-cols-4 gap-3 items-end fade-up" data-testid="goal-form">
          <div className="sm:col-span-2"><Label className="text-xs">Goal</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="goal-form-title" className="mt-1" placeholder="Score 500 runs this season" /></div>
          <div><Label className="text-xs">Metric</Label><select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm mt-1" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} data-testid="goal-form-metric">{METRICS.map((m) => <option key={m}>{m}</option>)}</select></div>
          <div><Label className="text-xs">Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid="goal-form-target" className="mt-1" /></div>
          <label className="flex items-center gap-2 text-sm sm:col-span-3"><input type="checkbox" checked={form.focus_area} onChange={(e) => setForm({ ...form, focus_area: e.target.checked })} data-testid="goal-form-focus" /> Mark as a focus area (max 3)</label>
          <Button onClick={add} className="bg-brand hover:bg-brand-dark" data-testid="goal-form-submit">Add goal</Button>
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 mb-3"><Target size={16} className="text-brand" /><span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Season Goals")}</span></div>
        {!goals ? <div className="skeleton h-24" /> : goals.length === 0 ? (
          <div className="ct-card p-8 text-center text-slate-500" data-testid="goals-empty">No goals yet. Set one to track your season.</div>
        ) : (
          <div className="space-y-3">
            {goals.map((g) => {
              const remaining = Math.max(0, g.target - g.current);
              return (
                <div key={g.id} className="ct-card p-4" data-testid={`goal-${g.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold flex items-center gap-2">{g.focus_area && <Flame size={14} className="text-brand" />}{g.title}</span>
                    <button onClick={() => del(g.id)} className="text-slate-300 hover:text-brand"><Trash2 size={15} /></button>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" data-testid="goal-progress-bar"><div className="h-full bg-brand transition-all duration-700" style={{ width: `${g.progress_pct}%` }} /></div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1"><span className="font-mono">{g.current} / {g.target} {g.metric}</span><span>{g.progress_pct}% · {remaining > 0 ? `${remaining} to go` : "Achieved!"}</span></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{t("Recent Report Cards")}</div>
        <div className="space-y-2">
          {entries.map((e) => (
            <Link key={e.id} to={`/app/entries/${e.id}`} className="ct-card p-4 flex justify-between items-center hover:border-brand transition-colors" data-testid={`report-link-${e.id}`}>
              <span className="font-semibold text-sm">{e.opponent || "Session"} <span className="text-slate-400 font-normal">· {e.date}</span></span>
              <span className="text-sm font-mono">{e.batting ? `${e.batting.runs}${e.batting.not_out ? "*" : ""}` : ""} {e.bowling ? `${e.bowling.wickets}/${e.bowling.runs_conceded}` : ""}</span>
            </Link>
          ))}
          {entries.length === 0 && <div className="ct-card p-6 text-center text-slate-500 text-sm">Log matches to unlock report cards.</div>}
        </div>
      </div>
    </div>
  );
}
